<template>
  <div class="grid p-fluid">
    <!-- TITULO / HEADER DE PÁGINA -->
    <div class="col-12 flex justify-content-between align-items-center mb-2">
      <div>
        <h4 class="m-0 text-900 font-bold">{{ isEdit ? 'Editar Promoción' : 'Nueva Promoción' }}</h4>
        <small class="text-600">Configura los parámetros, condiciones y beneficios de la regla comercial.</small>
      </div>
      <div class="flex gap-2">
        <Button label="Cancelar" icon="pi pi-times" class="p-button-outlined p-button-secondary " @click="cancelar" />
        <Button label="Guardar Promoción" icon="pi pi-check" class="p-button-success " @click="guardar" :loading="saving" />
      </div>
    </div>

    <!-- COLUMNA IZQUIERDA: PARAMETROS Y POS -->
    <div class="col-12 lg:col-4 flex flex-column gap-3">
      <!-- Card 1: Configuración Básica -->
      <div class="card p-3 surface-card border-round shadow-1">
        <div class="text-700 font-semibold mb-3 border-bottom-1 surface-border pb-2 flex justify-content-between align-items-center">
          <span><i class="pi pi-cog mr-2"></i>Datos Generales</span>
          <div class="flex align-items-center gap-2">
            <label for="estado" class="cursor-pointer">Activa</label>
            <InputSwitch v-model="promocion.estado" id="estado" />
          </div>
        </div>

        <div class="field mb-3">
          <label class="font-medium text-700">Nombre de la Promoción <span class="p-error">*</span></label>
          <InputText v-model="promocion.nombre" placeholder="Ej. Lunes de Pizza" class="" :class="{'p-invalid': submitted && !promocion.nombre}" />
          <small class="p-error" v-if="submitted && !promocion.nombre">Campo requerido.</small>
        </div>

        <div class="field mb-3">
          <label class="font-medium text-700">Canales Permitidos</label>
          <MultiSelect v-model="promocion.canales_id" :options="canalesVenta" optionLabel="nombre" optionValue="id" placeholder="Seleccionar (Vacío = Todos los canales)" class="w-full" display="chip" />
        </div>

        <div class="field mb-3">
          <label class="font-medium text-700">Día de la semana</label>
          <Dropdown v-model="promocion.dia_semana" :options="diasSemana" optionLabel="label" optionValue="value" placeholder="Cualquier día" class="" showClear />
        </div>

        <div class="formgrid grid">
          <div class="field col-6 mb-0">
            <label class="font-medium text-700">Fecha Inicio</label>
            <InputText type="date" v-model="promocion.fecha_inicio" class="" />
          </div>
          <div class="field col-6 mb-0">
            <label class="font-medium text-700">Fecha Fin</label>
            <InputText type="date" v-model="promocion.fecha_fin" class="" />
          </div>
        </div>
      </div>

      <!-- Card 2: POS e Imagen -->
      <div class="card p-3 surface-card border-round shadow-1">
        <div class="text-700 font-semibold mb-3 border-bottom-1 surface-border pb-2">
          <i class="pi pi-desktop mr-2"></i>Integración POS (Combo)
        </div>

        <div class="field mb-3">
          <label class="font-medium text-700">Grupo / Pestaña</label>
          <Dropdown v-model="promocion.grupo_id" :options="grupos" optionLabel="nombre" optionValue="id" placeholder="Seleccionar" class="" filter @change="promocion.subgrupo_id = null" />
        </div>

        <div class="field mb-3">
          <label class="font-medium text-700">Subgrupo / Sub-pestaña</label>
          <Dropdown v-model="promocion.subgrupo_id" :options="subgruposDelGrupoPos" optionLabel="nombre" optionValue="id" placeholder="Opcional" class="" filter showClear />
        </div>

        <div class="field mb-0">
          <label class="font-medium text-700 block mb-2">Imagen de la Promoción</label>
          <ZapiImageManager 
            :currentImage="promocion.imagen_pos"
            variant="logo"
            emptyText="Sin Imagen"
            emptyHint="Haz clic para subir (Recomendado 500x500)"
            @upload="onImageUpload"
            @remove="onImageRemove"
          />
        </div>
      </div>
    </div>

    <!-- COLUMNA DERECHA: REGLAS Y BENEFICIOS -->
    <div class="col-12 lg:col-8 flex flex-column gap-3">
      <!-- Card 3: Condición -->
      <div class="card p-3 surface-card border-round shadow-1">
        <div class="text-700 font-semibold mb-3 border-bottom-1 surface-border pb-2 text-primary flex align-items-center">
          <i class="pi pi-filter mr-2"></i>1. Condición Requerida
        </div>

        <div class="formgrid grid">
          <div class="field col-12 md:col-6">
            <label class="font-medium text-700">Tipo de Condición</label>
            <Dropdown v-model="condicion.tipo" :options="tiposCondicion" optionLabel="label" optionValue="value" class="" />
          </div>

          <div class="field col-12 md:col-6">
            <label class="font-medium text-700">Cantidad Mínima Requerida</label>
            <InputNumber v-model="condicion.cantidad_minima" showButtons :min="1" class="" />
          </div>

          <div class="field col-12 md:col-6" v-if="condicion.tipo === 'grupo' || condicion.tipo === 'repetido'">
            <label class="font-medium text-700">Grupo Requerido <span class="p-error">*</span></label>
            <Dropdown v-model="condicion_grupo_id" :options="grupos" optionLabel="nombre" optionValue="id" placeholder="Selecciona un grupo" class="" :class="{'p-invalid': submitted && !condicion_grupo_id}" filter @change="condicion_subgrupo_id = null" />
            <small class="p-error" v-if="submitted && !condicion_grupo_id">Campo requerido.</small>
          </div>

          <div class="field col-12 md:col-6" v-if="condicion.tipo === 'grupo' && subgruposParaCondicion.length > 0">
            <label class="font-medium text-700">Subgrupo Específico (Opcional)</label>
            <Dropdown v-model="condicion_subgrupo_id" :options="subgruposParaCondicion" optionLabel="nombre" optionValue="id" placeholder="Todos los del grupo" class="" filter showClear />
          </div>
        </div>
      </div>

      <!-- Card 4: Acción / Beneficio -->
      <div class="card p-3 surface-card border-round shadow-1">
        <div class="text-700 font-semibold mb-3 border-bottom-1 surface-border pb-2 text-green-600 flex align-items-center">
          <i class="pi pi-gift mr-2"></i>2. Beneficio a Entregar
        </div>

        <div class="formgrid grid">
          <div class="field col-12 md:col-6">
            <label class="font-medium text-700">¿A qué aplica el descuento?</label>
            <Dropdown v-model="accion.aplica_a" :options="tiposAccion" optionLabel="label" optionValue="value" class="" />
          </div>

          <div class="field col-12 md:col-6">
            <label class="font-medium text-700">Porcentaje de Descuento</label>
            <InputNumber v-model="accion.valor_descuento" prefix="%" :min="1" :max="100" class="" />
          </div>

          <div class="field col-12 md:col-6" v-if="accion.aplica_a === 'grupo'">
            <label class="font-medium text-700">Grupo a Descontar <span class="p-error">*</span></label>
            <Dropdown v-model="accion_grupo_id" :options="grupos" optionLabel="nombre" optionValue="id" placeholder="Selecciona un grupo" class="" :class="{'p-invalid': submitted && !accion_grupo_id}" filter @change="accion_subgrupo_id = null" />
            <small class="p-error" v-if="submitted && !accion_grupo_id">Campo requerido.</small>
          </div>

          <div class="field col-12 md:col-6" v-if="accion.aplica_a === 'grupo' && subgruposParaAccion.length > 0">
            <label class="font-medium text-700">Subgrupo Específico (Opcional)</label>
            <Dropdown v-model="accion_subgrupo_id" :options="subgruposParaAccion" optionLabel="nombre" optionValue="id" placeholder="Todos los del grupo" class="" filter showClear />
          </div>

          <!-- <div class="field col-12 md:col-6" v-if="condicion.tipo === 'repetido'">
            <label class="font-medium text-700">Unidad Objetivo (Ej. La 2da unidad)</label>
            <InputNumber v-model="accion.unidad_objetivo" showButtons :min="2" class="" />
          </div> -->

          <div class="field col-12 md:col-6">
            <label class="font-medium text-700">Límite de Beneficios (Por Combo)</label>
            <InputNumber v-model="accion.max_aplicaciones_carrito" placeholder="Sin límite" :min="1" class="" />
            <span class="text-500 mt-1 block">Vacío = Ilimitado</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toast />
</template>

<script>
import PromotionService from '@/service/PromotionService';
import GrupoService from '@/service/GrupoService';
import SubGrupoService from '@/service/SubGrupoService';
import CanalVentaService from '@/service/CanalVentaService';
import ZapiImageManager from '@/components/shared/ZapiImageManager.vue';

export default {
  components: {
    ZapiImageManager
  },
  data() {
    return {
      isEdit: false,
      submitted: false,
      saving: false,
      promocion: {
        nombre: '',
        dia_semana: null,
        fecha_inicio: null,
        fecha_fin: null,
        estado: true,
        mostrar_en_pos: true, // Siempre true para ventas en POS
        grupo_id: null,
        subgrupo_id: null,
        imagen_pos: null,
        canales_id: []
      },
      condicion: {
        tipo: 'grupo', // 'grupo', 'repetido'
        target_id: null,
        cantidad_minima: 1
      },
      accion: {
        aplica_a: 'grupo', // 'grupo', 'extra', 'borde', 'producto'
        target_id: null,
        valor_descuento: 50,
        unidad_objetivo: null,
        max_aplicaciones_carrito: null
      },
      // Variables UI para selección jerárquica
      condicion_grupo_id: null,
      condicion_subgrupo_id: null,
      accion_grupo_id: null,
      accion_subgrupo_id: null,
      grupos: [],
      subgrupos: [],
      canalesVenta: [],
      diasSemana: [
        { label: 'Lunes', value: 1 }, { label: 'Martes', value: 2 },
        { label: 'Miércoles', value: 3 }, { label: 'Jueves', value: 4 },
        { label: 'Viernes', value: 5 }, { label: 'Sábado', value: 6 },
        { label: 'Domingo', value: 7 }
      ],
      tiposCondicion: [
        { label: 'Comprar de un Grupo/Subgrupo', value: 'grupo' },
        { label: 'Comprar el mismo producto N veces (2x1)', value: 'repetido' }
      ],
      tiposAccion: [
        { label: 'Descuento a Grupo/Subgrupo', value: 'grupo' },
        { label: 'Descuento a Extras de la Pizza', value: 'extra' },
        { label: 'Descuento a Bordes', value: 'borde' },
        { label: 'Descuento a la misma unidad (2x1)', value: 'producto' }
      ]
    };
  },
  created() {
    this.promotionService = new PromotionService();
    this.grupoService = new GrupoService();
    this.subGrupoService = new SubGrupoService();
    this.canalVentaService = new CanalVentaService();
  },
  computed: {
    // Computa dinámicamente los subgrupos del grupo seleccionado para el POS
    subgruposDelGrupoPos() {
      if (!this.promocion.grupo_id || !Array.isArray(this.subgrupos)) return [];
      return this.subgrupos.filter(sg => sg.grupo_id == this.promocion.grupo_id);
    },
    subgruposParaCondicion() {
      if (!this.condicion_grupo_id || !Array.isArray(this.subgrupos)) return [];
      return this.subgrupos.filter(sg => sg.grupo_id == this.condicion_grupo_id);
    },
    subgruposParaAccion() {
      if (!this.accion_grupo_id || !Array.isArray(this.subgrupos)) return [];
      return this.subgrupos.filter(sg => sg.grupo_id == this.accion_grupo_id);
    }
  },
  mounted() {
    Promise.all([
      this.grupoService.getGrupoAll(),
      this.subGrupoService.getSubGrupoAll(),
      this.canalVentaService.getCanalVentaSelect() // Cargamos los canales de venta disponibles
    ]).then(([resGrupos, resSubgrupos, resCanales]) => {
      this.grupos = resGrupos.grupos || resGrupos.data || resGrupos || [];
      this.subgrupos = resSubgrupos.subgrupos || resSubgrupos.sub_grupos || resSubgrupos.data || resSubgrupos || [];
      this.canalesVenta = resCanales.canales_venta || resCanales.canalventa || resCanales.data || [];
      
      const id = this.$route.params.id;
      if (id) {
        this.isEdit = true;
        this.cargarPromocion(id);
      }
    }).catch(e => console.error("Error inicializando datos", e));
  },
  methods: {
    onImageUpload(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.promocion.imagen_pos = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    onImageRemove() {
      this.promocion.imagen_pos = null;
    },
    cargarPromocion(id) {
      this.promotionService.getPromotion(id).then(response => {
        const data = response.data || response;
        this.promocion = {
          id: data.id,
          nombre: data.nombre,
          dia_semana: data.dia_semana,
          fecha_inicio: data.fecha_inicio ? data.fecha_inicio.substring(0, 10) : null,
          fecha_fin: data.fecha_fin ? data.fecha_fin.substring(0, 10) : null,
          estado: data.estado ? true : false,
          mostrar_en_pos: true, // Forzamos siempre a mostrar en POS
          grupo_id: data.grupo_id || null,
          subgrupo_id: data.subgrupo_id || null,
          imagen_pos: data.imagen_pos || null,
          canales_id: data.canales_id || [] // Cargamos los canales de venta guardados
        };
        
        if (data.conditions && data.conditions.length > 0) {
          const c = data.conditions[0];
          this.condicion = {
            tipo: c.tipo === 'subgrupo' ? 'grupo' : c.tipo,
            cantidad_minima: c.cantidad_minima
          };
          if (c.tipo === 'grupo') {
            this.condicion_grupo_id = c.target_id;
            this.condicion_subgrupo_id = null;
          } else if (c.tipo === 'subgrupo') {
            this.condicion_subgrupo_id = c.target_id;
            const sg = this.subgrupos.find(s => s.id === c.target_id || s.sub_id === c.target_id);
            if (sg) this.condicion_grupo_id = sg.grupo_id;
          } else {
            this.condicion_grupo_id = c.target_id;
          }
        }
        
        if (data.actions && data.actions.length > 0) {
          const a = data.actions[0];
          this.accion = {
            aplica_a: a.aplica_a === 'subgrupo' ? 'grupo' : a.aplica_a,
            valor_descuento: parseFloat(a.valor_descuento),
            unidad_objetivo: a.unidad_objetivo,
            max_aplicaciones_carrito: a.max_aplicaciones_carrito
          };
          if (a.aplica_a === 'grupo') {
            this.accion_grupo_id = a.target_id;
            this.accion_subgrupo_id = null;
          } else if (a.aplica_a === 'subgrupo') {
            this.accion_subgrupo_id = a.target_id;
            const sg = this.subgrupos.find(s => s.id === a.target_id || s.sub_id === a.target_id);
            if (sg) this.accion_grupo_id = sg.grupo_id;
          } else {
            this.accion_grupo_id = a.target_id;
          }
        }
      });
    },
    cancelar() {
      this.$router.push({ name: 'promociones' });
    },
    guardar() {
      this.submitted = true;
      if (!this.promocion.nombre) return;
      if ((this.condicion.tipo === 'grupo' || this.condicion.tipo === 'repetido') && !this.condicion_grupo_id) return;
      if (this.accion.aplica_a === 'grupo' && !this.accion_grupo_id) return;
      
      this.saving = true;

      // Armar Condición
      let cond_tipo_backend = this.condicion.tipo;
      let cond_target_id = this.condicion_grupo_id;
      if (this.condicion.tipo === 'grupo') {
        if (this.condicion_subgrupo_id) {
          cond_tipo_backend = 'subgrupo';
          cond_target_id = this.condicion_subgrupo_id;
        }
      }
      
      // Armar Acción
      let acc_aplica_backend = this.accion.aplica_a;
      let acc_target_id = this.accion_grupo_id;
      if (this.accion.aplica_a === 'grupo') {
        if (this.accion_subgrupo_id) {
          acc_aplica_backend = 'subgrupo';
          acc_target_id = this.accion_subgrupo_id;
        }
      } else if (this.accion.aplica_a === 'extra' || this.accion.aplica_a === 'borde') {
        acc_target_id = null;
      }

      const payload = {
        ...this.promocion,
        conditions: [{
          tipo: cond_tipo_backend,
          target_id: cond_target_id,
          cantidad_minima: this.condicion.cantidad_minima
        }],
        actions: [{
          aplica_a: acc_aplica_backend,
          target_id: acc_target_id,
          valor_descuento: this.accion.valor_descuento,
          unidad_objetivo: this.accion.unidad_objetivo || 1,
          max_aplicaciones_carrito: this.accion.max_aplicaciones_carrito
        }]
      };
      
      this.promotionService.savePromotion(payload).then(() => {
        this.$toast.add({severity:'success', summary: 'Éxito', detail: 'Promoción guardada', life: 3000});
        setTimeout(() => this.cancelar(), 1000);
      }).catch(err => {
        console.error(err);
        this.$toast.add({severity:'error', summary: 'Error', detail: 'Revise los campos e intente de nuevo', life: 3000});
      }).finally(() => {
        this.saving = false;
      });
    }
  }
}
</script>
